import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api/v1`;

// LEGACY localStorage token key — kept here only so we can purge it from
// older sessions during the httpOnly-cookie migration. The auth JWT now
// lives in a Secure;HttpOnly;SameSite=None cookie (`chalkos_session`) set
// by the backend on signin/signup/magic-consume/join.
const LEGACY_TOKEN_KEY = "chalkos.token";
const USER_KEY = "chalkos.user";
// New: a parallel non-httpOnly token mirror used ONLY as a fallback when
// the cookie can't be delivered (e.g. cross-origin response where the
// platform ingress overrides Access-Control-Allow-Origin to "*" and the
// browser refuses to set the cookie). The token is sent in the
// Authorization header on every subsequent request, so authentication
// works EITHER via the cookie OR via the header — whichever the
// environment can deliver. This is a graceful, opt-in degradation:
// when CORS works the header is redundant; when CORS is misconfigured
// the cookie is silently dropped and the header carries the JWT instead.
const FALLBACK_TOKEN_KEY = "chalkos.session";

export function getStoredUser() {
  const raw = localStorage.getItem(USER_KEY);
  return raw ? JSON.parse(raw) : null;
}
export function setStoredUser(u) {
  if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
  else localStorage.removeItem(USER_KEY);
}

function getFallbackToken() {
  try { return localStorage.getItem(FALLBACK_TOKEN_KEY) || null; }
  catch { return null; }
}
function setFallbackToken(t) {
  try {
    if (t) localStorage.setItem(FALLBACK_TOKEN_KEY, t);
    else localStorage.removeItem(FALLBACK_TOKEN_KEY);
  } catch { /* private mode etc — non-fatal */ }
}

// One-time cleanup: scrub any JWT left in localStorage by a pre-cookie session.
try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch { /* ignore */ }

// withCredentials sends the httpOnly auth cookie on every request. When the
// platform ingress overrides Access-Control-Allow-Origin to `*`, modern
// browsers reject the response on credentialed requests — including the
// auth POST itself — and we can never read the token. We mitigate by
// running auth POSTs through a separate non-credentialed client below.
const client = axios.create({ baseURL: API_BASE, withCredentials: true });
export { client };

// Auth client: identical to `client` BUT with withCredentials=false. This
// turns the auth POST into a non-credentialed simple-CORS request, which
// the browser accepts even when Access-Control-Allow-Origin is `*`. The
// JWT returned in the response body is captured client-side (via
// `setFallbackToken`) and sent on every subsequent request as
// `Authorization: Bearer <token>` by the interceptor above. This works
// regardless of cookie / ingress CORS state.
const authClient = axios.create({ baseURL: API_BASE, withCredentials: false });

// Attach Authorization: Bearer <fallback token> when one is stored. Harmless
// when the cookie is also delivering the JWT (the backend reads cookie first).
client.interceptors.request.use((cfg) => {
  const t = getFallbackToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    if (!cfg.headers.Authorization) {
      cfg.headers.Authorization = `Bearer ${t}`;
    }
  }
  return cfg;
});

// Retry once on transient gateway errors (502/503/504) and on outright network
// failures (no `response`). These show up during pod restarts, edge cache
// purges, or brief origin blips. A single 700ms-backoff retry hides the
// vast majority of them without slowing the happy path.
//
// IMPORTANT: this retry interceptor is attached to the credentialed `client`
// only, NOT to `authClient`. Auth POSTs (signin / signup / forgot / magic-link)
// are non-idempotent in their side effects — every retry sends another
// password-reset email, signs another auth event, etc. The
// non-credentialed `authClient` never silently retries.
const GATEWAY_STATUSES = new Set([502, 503, 504]);
const SAFE_RETRY_METHODS = new Set(["get", "post", "put", "patch", "delete", "options", "head"]);
client.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config || {};
  if (cfg.__retried) throw error;
  const status = error.response && error.response.status;
  const isGateway = GATEWAY_STATUSES.has(status);
  const isNetwork = !error.response;
  const method = (cfg.method || "get").toLowerCase();
  // Only retry truly safe methods (idempotent reads).
  const retryable = SAFE_RETRY_METHODS.has(method) && method !== "post";
  if ((isGateway || isNetwork) && retryable) {
    cfg.__retried = true;
    await new Promise((r) => setTimeout(r, 700));
    return client(cfg);
  }
  throw error;
});

// Codes we never want to surface raw to users — these always need to map
// to an UpgradeGateCard or a checkout flow at the component layer. The
// sanitizer below rewrites the .message so any component that DOES fall
// through and surfaces err.message gets a polite line instead of dev-talk.
const SOFT_GATE_CODES = new Set(["TIER_INSUFFICIENT", "PERMISSION_DENIED"]);
function _sanitize(err) {
  if (!err) return err;
  if (err.code === "SECONDARY_TEAM_LOCKED") {
    err.message = "Managing multiple teams is part of The Chalkboard. Upgrade for $11.99/month.";
  } else if (SOFT_GATE_CODES.has(err.code)) {
    err.message = "This feature is part of The Chalkboard. Upgrade for $11.99/month.";
  }
  return err;
}

// Normalize the API envelope. Throws an Error with .code, .message on failure.
export async function unwrap(p) {
  try {
    const res = await p;
    const body = res.data;
    if (body && body.success === false) {
      const err = new Error((body.error && body.error.message) || "Something went wrong.");
      err.code = (body.error && body.error.code) || "ERROR";
      err.meta = (body.error && body.error.meta) || {};
      throw _sanitize(err);
    }
    return body && body.data;
  } catch (e) {
    // Map raw gateway / network failures to a friendly message before
    // bubbling up. The auto-retry above already gave them one shot, so if
    // we land here the origin really is unreachable.
    const status = e.response && e.response.status;
    if (status === 503 || status === 502 || status === 504) {
      const err = new Error("ChalkOS is briefly unreachable. Try again in a few seconds.");
      err.code = "GATEWAY_UNAVAILABLE";
      err.status = status;
      throw err;
    }
    if (!e.response) {
      const err = new Error("Couldn't reach ChalkOS. Check your connection and try again.");
      err.code = "NETWORK_OFFLINE";
      throw err;
    }
    if (e.response && e.response.data && e.response.data.error) {
      const err = new Error(e.response.data.error.message || "Something went wrong.");
      err.code = e.response.data.error.code || "ERROR";
      err.status = e.response.status;
      err.meta = e.response.data.error.meta || {};
      throw _sanitize(err);
    }
    throw e;
  }
}

// CORS preflight-skip helper.
//
// A POST is treated by browsers as a CORS "simple request" (no OPTIONS
// preflight) if and only if the Content-Type is `text/plain`,
// `application/x-www-form-urlencoded`, or `multipart/form-data`. Auth
// endpoints in production go to a cross-origin host whose Cloudflare layer
// occasionally takes 6+ seconds to respond to OPTIONS preflight — iOS Safari
// times the request out and the user sees a 5xx-shaped failure.
//
// To bypass that entirely for the entry-point auth requests, we send a
// JSON-encoded body but stamp the Content-Type as `text/plain`. The backend
// parses it via `_parse_simple_or_json` and validates with Pydantic.
const _SIMPLE_HEADERS = { "Content-Type": "text/plain;charset=UTF-8" };
function _simple(payload) {
  // Send a plain string. Axios won't auto-set JSON content-type when the
  // body is a string and Content-Type is already supplied.
  return { data: JSON.stringify(payload || {}), headers: _SIMPLE_HEADERS };
}

// Helper for credentialed-API endpoints that need to bypass the production
// ingress CORS misconfig (Allow-Origin: * with credentials = browser refuses
// the response). Sends Authorization: Bearer via the non-credentialed
// authClient, so the response is accepted regardless of cookie state.
function _bearer() {
  return { Authorization: `Bearer ${getFallbackToken() || ""}` };
}

export const api = {
  // Auth — sent through the NON-CREDENTIALED `authClient` and as CORS-simple
  // text/plain bodies, so even a misconfigured ingress (Allow-Origin: `*`
  // with credentials, no specific allowlist) can't block them. The JWT comes
  // back in the response body and is persisted via `setFallbackToken` for
  // every subsequent (credentialed) request to attach as
  // `Authorization: Bearer …`. NO retries on these — every retry has a
  // user-visible side effect (extra emails, extra signup attempts, etc).
  signin: async (email, password) => {
    const data = await unwrap(authClient.post("/auth/signin", _simple({ email, password }).data, { headers: _SIMPLE_HEADERS }));
    if (data && data.token) setFallbackToken(data.token);
    return data;
  },
  signup: async (payload) => {
    const data = await unwrap(authClient.post("/auth/signup", _simple(payload).data, { headers: _SIMPLE_HEADERS }));
    if (data && data.token) setFallbackToken(data.token);
    return data;
  },
  signout: async () => {
    try { return await unwrap(client.post("/auth/signout")); }
    finally { setFallbackToken(null); }
  },
  me: () => unwrap(client.get("/auth/me")),
  magicLink: (email) => unwrap(authClient.post("/auth/magic-link", _simple({ email }).data, { headers: _SIMPLE_HEADERS })),
  magicConsume: async (token) => {
    const data = await unwrap(authClient.post("/auth/magic-consume", _simple({ token }).data, { headers: _SIMPLE_HEADERS }));
    if (data && data.token) setFallbackToken(data.token);
    return data;
  },
  forgot: (email) => unwrap(authClient.post("/auth/forgot", _simple({ email }).data, { headers: _SIMPLE_HEADERS })),
  reset: (token, new_password) => unwrap(authClient.post("/auth/reset", _simple({ token, new_password }).data, { headers: _SIMPLE_HEADERS })),
  join: async (payload) => {
    const data = await unwrap(authClient.post("/auth/join", _simple(payload).data, { headers: _SIMPLE_HEADERS }));
    if (data && data.token) setFallbackToken(data.token);
    return data;
  },
  passwordStrength: (p) => unwrap(client.get(`/auth/password-strength`, { params: { p } })),
  // Switch the current user's account role (coach ↔ guardian). Returns the
  // fresh JWT so we can swap the stored token (role lives inside the JWT).
  switchRole: async (role) => {
    const data = await unwrap(authClient.post(
      "/auth/role",
      _simple({ role }).data,
      {
        headers: {
          ..._SIMPLE_HEADERS,
          Authorization: `Bearer ${getFallbackToken() || ""}`,
        },
      },
    ));
    if (data && data.token) setFallbackToken(data.token);
    return data;
  },

  // Teams
  listTeams: () => unwrap(client.get("/teams")),
  createTeam: (payload) => unwrap(client.post("/teams", payload)),
  roleOptions: () => unwrap(client.get("/auth/role-options")),
  getTeam: (id) => unwrap(client.get(`/teams/${id}`)),
  tierFor: (id) => unwrap(client.get(`/teams/${id}/tier`)),
  joinByCode: (code) => unwrap(client.post("/teams/join-by-code", { code })),
  startPrefill: (query) => unwrap(client.get(`/start/prefill?${query}`)),
  startConfirm: (body) => unwrap(client.post("/start/confirm", body)),
  redeemKit: (code) => unwrap(client.post("/kit/redeem", { code })),
  getEmailPrefs: () => unwrap(client.get("/me/email-prefs")),
  setEmailPrefs: (marketing) => unwrap(client.post("/me/email-prefs", { marketing })),
  adminKitCodes: () => unwrap(client.get("/admin/kit-codes")),
  adminVoluntoldFunnel: (q = "") => unwrap(client.get(`/admin/funnel/voluntold${q}`)),
  listJoinRequests: (teamId) => unwrap(client.get(`/teams/${teamId}/join-requests`)),
  listStaff: (teamId) => unwrap(client.get(`/staff/${teamId}`)),
  inviteStaff: (teamId, body) => unwrap(client.post(`/staff/${teamId}/invites`, body)),
  revokeStaffInvite: (teamId, inviteId) => unwrap(client.delete(`/staff/${teamId}/invites/${inviteId}`)),
  updateStaffPermissions: (teamId, userId, body) => unwrap(client.patch(`/staff/${teamId}/members/${userId}`, body)),
  removeStaff: (teamId, userId) => unwrap(client.delete(`/staff/${teamId}/members/${userId}`)),
  previewStaffInvite: (token) => unwrap(client.get(`/staff/invites/${encodeURIComponent(token)}`)),
  acceptStaffInvite: (token) => unwrap(client.post(`/staff/invites/${encodeURIComponent(token)}/accept`)),
  decideJoinRequest: (requestId, action) =>
    unwrap(client.post(`/teams/join-requests/${requestId}/decide`, { action })),

  // Roster
  listRoster: (teamId) => unwrap(client.get(`/roster/${teamId}`)),
  addAthlete: (teamId, body) => unwrap(client.post(`/roster/${teamId}/athletes`, body)),
  inviteGuardian: (teamId, body) => unwrap(client.post(`/roster/${teamId}/guardians/invite`, body)),
  importRoster: (teamId, rows) => unwrap(client.post(`/roster/${teamId}/import`, { rows })),
  exportRoster: (teamId) => unwrap(client.get(`/roster/${teamId}/export`)),
  listRosterInvites: (teamId) => unwrap(client.get(`/roster/${teamId}/invites`)),
  resendRosterInvite: (teamId, inviteId) =>
    unwrap(client.post(`/roster/${teamId}/invites/${inviteId}/resend`)),
  // V1.5
  createPoll: (teamId, body) => unwrap(client.post(`/v15/teams/${teamId}/polls`, body)),
  getPoll: (pollId) => unwrap(client.get(`/v15/polls/${pollId}`)),
  votePoll: (pollId, optionIds) => unwrap(client.post(`/v15/polls/${pollId}/vote`, { option_ids: optionIds })),
  pinMessage: (messageId) => unwrap(client.post(`/v15/messages/${messageId}/pin`)),
  unpinMessage: (messageId) => unwrap(client.post(`/v15/messages/${messageId}/unpin`)),
  markMessageRead: (messageId) => unwrap(client.post(`/v15/messages/${messageId}/read`)),
  messageViewers: (messageId) => unwrap(client.get(`/v15/messages/${messageId}/viewers`)),
  eventWeather: (eventId) => unwrap(client.get(`/v15/events/${eventId}/weather`)),
  updateAthlete: (teamId, athleteId, body) =>
    unwrap(client.patch(`/roster/${teamId}/athletes/${athleteId}`, body)),
  removeAthlete: (teamId, athleteId) =>
    unwrap(client.delete(`/roster/${teamId}/athletes/${athleteId}`)),

  // Events / messages / RSVPs
  listEvents: (teamId) => unwrap(client.get(`/events/${teamId}`)),
  createEvent: (teamId, body) => unwrap(client.post(`/events/${teamId}`, body)),
  createRecurring: (teamId, body) => unwrap(client.post(`/events/${teamId}/recurring`, body)),
  cancelEvent: (eventId, reason) => unwrap(client.post(`/events/${eventId}/cancel-notify`, { reason })),
  delayEvent: (eventId, note) => unwrap(client.post(`/events/${eventId}/delay-notify`, { note })),
  rescheduleEvent: (eventId, startLocal) => unwrap(client.post(`/events/${eventId}/reschedule-notify`, { start_local: startLocal })),
  checkConflicts: (teamId, body) => unwrap(client.post(`/events/${teamId}/check-conflicts`, body)),
  bulkEvents: (teamId, body) => unwrap(client.post(`/events/${teamId}/bulk`, body)),

  setRsvp: (eventId, athleteId, status, note) =>
    unwrap(client.post(`/rsvps/${eventId}`, { athlete_id: athleteId, status, note })),
  myRsvpsForEvent: (eventId) => unwrap(client.get(`/rsvps/me/event/${eventId}`)),
  listRsvps: (eventId) => unwrap(client.get(`/rsvps/${eventId}`)),

  // Chalk Talk
  listMessages: (teamId, before) => unwrap(client.get(`/chalk-talk/${teamId}`, { params: before ? { before } : {} })),
  postMessage: (teamId, body) => unwrap(client.post(`/chalk-talk/${teamId}`, body)),
  markMessageRead: (messageId) => unwrap(client.post(`/chalk-talk/${messageId}/read`)),
  // Pin/unpin via the V1.5 tier-gated endpoints (Pickup max 1, Chalkboard max 5).
  togglePin: (messageId, pinned) => unwrap(client.post(
    `/v15/messages/${messageId}/${pinned ? "pin" : "unpin"}`,
  )),

  // Chalk Lines (files)
  listFiles: (teamId) => unwrap(client.get(`/files/${teamId}`)),
  fileUsage: (teamId) => unwrap(client.get(`/files/${teamId}/usage`)),
  uploadFile: (teamId, file) => {
    const form = new FormData();
    form.append("file", file);
    return unwrap(client.post(`/files/${teamId}`, form, { headers: { "Content-Type": "multipart/form-data" } }));
  },
  deleteFile: (teamId, fileId) => unwrap(client.delete(`/files/${teamId}/${fileId}`)),

  // AI
  aiChips: () => unwrap(client.get("/ai/chips")),
  aiDraft: (chip, prompt, team_id) => unwrap(client.post("/ai/draft", { chip, prompt, team_id })),
  aiEraseDraft: (eventTitle, reasonHint) => unwrap(client.post("/ai/erase-draft", { event_title: eventTitle, reason_hint: reasonHint })),

  // Notifications
  banners: () => unwrap(client.get("/notifications/banners")),
  dismissBanner: (id) => unwrap(client.post(`/notifications/banners/${id}/dismiss`)),
  getPrefs: () => unwrap(client.get("/notifications/prefs")),
  setPrefs: (prefs) => unwrap(client.put("/notifications/prefs", prefs)),

  // Erase / Delay
  delayEvent: (eventId, note) => unwrap(client.post(`/events/${eventId}/delay-notify`, { note })),

  // Volunteers
  listVolunteers: (teamId) => unwrap(client.get(`/volunteers/${teamId}`)),
  createDuty: (teamId, body) => unwrap(client.post(`/volunteers/${teamId}`, body)),
  bulkRotation: (teamId, body) => unwrap(client.post(`/volunteers/${teamId}/bulk`, body)),
  claimDuty: (volId) => unwrap(client.post(`/volunteers/${volId}/claim`)),
  deleteDuty: (volId) => unwrap(client.delete(`/volunteers/${volId}`)),

  // Season archive
  archiveSeason: (teamId, body) => unwrap(client.post(`/teams/${teamId}/archive`, body)),

  // League
  leagueOverview: () => unwrap(client.get("/league/overview")),
  leagueBroadcast: (body) => unwrap(client.post("/league/broadcast", { body })),

  // Subscriptions — checkout/portal routed through non-credentialed authClient
  // + Bearer so the production CORS-with-credentials trap can't break them.
  mySubscription: () => unwrap(client.get("/subscriptions/me")),
  familyDashboard: () => unwrap(client.get("/family/dashboard")),
  // Family Dashboard — Clipboard+ manual entries (parent self-add).
  manualTeams: () => unwrap(client.get("/family/manual/teams")),
  createManualTeam: (body) => unwrap(client.post("/family/manual/teams", body)),
  updateManualTeam: (id, body) => unwrap(client.patch(`/family/manual/teams/${id}`, body)),
  deleteManualTeam: (id) => unwrap(client.delete(`/family/manual/teams/${id}`)),
  createManualAthlete: (body) => unwrap(client.post("/family/manual/athletes", body)),
  deleteManualAthlete: (id) => unwrap(client.delete(`/family/manual/athletes/${id}`)),
  manualEvents: () => unwrap(client.get("/family/manual/events")),
  createManualEvent: (body) => unwrap(client.post("/family/manual/events", body)),
  deleteManualEvent: (id) => unwrap(client.delete(`/family/manual/events/${id}`)),
  inviteCoachToChalkOS: (body) => unwrap(client.post("/family/manual/invite-coach", body)),
  // Roster merge — coach side.
  mergeCandidates: (teamId) => unwrap(client.get(`/roster/${teamId}/merge-candidates`)),
  mergeProfiles: (teamId, body) => unwrap(client.post(`/roster/${teamId}/merge`, body)),
  updateProfile: (body) => unwrap(client.patch("/users/me", body)),
  familyCalendarToken: () => unwrap(client.get("/family/calendar/token")),
  demoRenew: () => unwrap(client.post("/subscriptions/demo-renew")),
  createCheckout: (body) => unwrap(authClient.post(
    "/subscriptions/checkout",
    JSON.stringify(body || {}),
    { headers: { ..._SIMPLE_HEADERS, ..._bearer() } },
  )),
  openPortal: () => unwrap(authClient.post(
    "/subscriptions/portal",
    "",
    { headers: { ..._SIMPLE_HEADERS, ..._bearer() } },
  )),
  getCheckoutSession: (sessionId) => unwrap(client.get(`/subscriptions/checkout/${sessionId}`)),
  previewCompleteCheckout: (sessionId) => unwrap(client.post(
    "/subscriptions/checkout/preview-complete",
    { session_id: sessionId },
  )),

  // Marketing
  captureLead: (body) => unwrap(client.post("/marketing/leads", body)),

  // Web Push
  pushVapidPublicKey: () => unwrap(client.get("/push/vapid-public-key")),
  pushSubscribe: (body) => unwrap(client.post("/push/subscribe", body)),
  pushUnsubscribe: (body) => unwrap(client.delete("/push/subscribe", { data: body })),
  adminListLeads: () => unwrap(client.get("/admin/marketing/leads")),
  adminOverview: () => unwrap(client.get("/admin/overview")),
  adminFunnel: (days = 30) => unwrap(client.get("/admin/funnel", { params: { days } })),
  adminListUsers: () => unwrap(client.get("/admin/users")),
  adminDisableUser: (user_id, disabled = true) => unwrap(client.post(`/admin/users/${user_id}/disable`, { disabled })),
  adminDeleteUser: (user_id) => unwrap(client.post(`/admin/users/${user_id}/delete`)),
  adminRestoreUser: (user_id) => unwrap(client.post(`/admin/users/${user_id}/restore`)),

  // Admin
  adminBilling: () => unwrap(client.get("/admin/billing")),
  adminUpgrade: (user_id, tier) => unwrap(client.post("/admin/billing/upgrade", { user_id, tier })),
  adminDowngrade: (user_id) => unwrap(client.post("/admin/billing/downgrade", { user_id })),
  adminRefundAndCancel: (user_id, reason, note) => unwrap(client.post("/admin/billing/refund-and-cancel", { user_id, reason, note })),
  adminRefundReasons: (days = 90) => unwrap(client.get("/admin/billing/refund-reasons", { params: { days } })),
  adminStartTrial: (user_id, tier = "chalkboard") => unwrap(client.post("/admin/billing/start-trial", { user_id, tier })),
  adminExpireTrial: (user_id) => unwrap(client.post("/admin/billing/expire-trial", { user_id })),
  adminSweepTrials: () => unwrap(client.post("/admin/billing/sweep-trials")),
  adminDemoRenew: (user_id) => unwrap(client.post("/admin/billing/demo-renew", { user_id })),
  adminEmails: () => unwrap(client.get("/admin/emails")),
  adminNotifications: () => unwrap(client.get("/admin/notifications")),
  adminJobs: () => unwrap(client.get("/admin/jobs")),
  adminTestJob: () => unwrap(client.post("/admin/jobs/test")),
  adminAudit: () => unwrap(client.get("/admin/audit")),
  adminPermissionTests: () => unwrap(client.get("/admin/permission-tests")),
};

export default client;
